import { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useNotificationStore } from '@/stores/notificationStore';
import { useMembershipStore, useAssetRenewStore } from '@/stores';
import { fetchGetMyApplications } from '@/service/api';

import { NavbarRenderedContext } from './navbar-context';
import { SystemLogo } from '@/components/common';
import { UserMenu } from './UserMenu';
import MembershipModal from '@/components/membership';
import NotificationPopover from './NotificationPopover';
import UserPointsBadge from './UserPointsBadge';

const navLinks = [
  { label: '首页', href: '/', hasSparkle: false },
  { label: '需求大厅', href: '/demands', hasSparkle: true },
  { label: '人才广场', href: '/talents', hasSparkle: true },
  { label: '学习大厅', href: '/courses', hasSparkle: true },
  { label: '社区', href: '/community', hasSparkle: true },
  { label: '发行服务', href: '/distribution', hasSparkle: true },
];

/** 四角星闪烁 Icon */
function SparkleIcon({ className = 'w-3 h-3 text-[#facc15]' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
    </svg>
  );
}

function NavbarContent() {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    isOpen: vipModalOpen,
    openMembershipModal,
    closeMembershipModal,
    defaultTab,
    defaultMemberType,
  } = useMembershipStore();
  const [navigatingDistribution, setNavigatingDistribution] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, user, rawUser, logout } = useAuth();
  const { startPolling, stopPolling } = useNotificationStore();

  const isHomePage = location.pathname === '/';

  const activeNavIndex = navLinks.findIndex((link) => {
    if (link.label === '发行服务') {
      return location.pathname.startsWith('/distribution') || location.pathname.startsWith('/projects');
    }
    return link.href === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(link.href);
  });

  const handleDistributionClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      navigate('/distribution');
      return;
    }
    if (navigatingDistribution) return;
    setNavigatingDistribution(true);
    try {
      const res = await fetchGetMyApplications();
      const list = Array.isArray(res) ? res : res?.items || [];
      if (list && list.length > 0) {
        navigate('/projects');
      } else {
        navigate('/distribution');
      }
    } catch {
      navigate('/distribution');
    } finally {
      setNavigatingDistribution(false);
    }
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // 监听登录状态，开启/停止未读消息数轮询
  useEffect(() => {
    if (isLoggedIn) {
      startPolling(30000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [isLoggedIn, startPolling, stopPolling]);

  // 从账号信息 rawUser.membership.wallet 中读取积分钱包信息
  const wallet = rawUser?.membership?.wallet;
  const membershipPoints = wallet?.membership_balance ?? 0;
  const purchasedPoints = wallet?.purchased_balance ?? 0;
  const totalPoints = wallet?.total_balance ?? 0;

  // 判断是否拥有终身免费/无限积分权益 (POINT_CONTENT_UNLIMITED)
  const isUnlimitedPoints = Boolean(
    rawUser?.membership?.subscriptions?.[0]?.benefits_snapshot?.POINT_CONTENT_UNLIMITED
  );

  const userAvatarCache = useAssetRenewStore((state) => state.userAvatarCache);

  const handleLogout = () => {
    logout();
  };

  const avatarUrl = userAvatarCache?.url || (typeof user?.avatar === 'object'
    ? (user.avatar as any)?.access_url
    : (typeof user?.avatar === 'string' && (user.avatar.startsWith('http') || user.avatar.startsWith('data:') || user.avatar.startsWith('blob:') || user.avatar.includes('/'))
      ? user.avatar
      : (rawUser?.avatar as any)?.access_url));



  return (
    <header
      className={`fixed left-0 right-0 z-50 flex justify-center pointer-events-none transition-all duration-300 ${isHomePage
        ? 'top-4 sm:top-6 px-3 sm:px-6 lg:px-8'
        : 'top-0 px-0'
        }`}
    >
      <div
        className={`pointer-events-auto w-full flex items-center justify-between transition-all duration-300 ${isHomePage
          ? 'max-w-[1400px] rounded-full px-4 sm:px-6 lg:px-8 py-1 sm:py-1.5 bg-white/85 backdrop-blur-2xl border-2 sm:border-[3px] border-white shadow-[0_10px_35px_rgba(0,0,0,0.06),0_0_0_1px_rgba(255,255,255,0.9)]'
          : 'max-w-none rounded-none px-4 sm:px-6 lg:px-8 h-14 sm:h-16 bg-white border-b border-slate-200/80 shadow-sm'
          }`}
      >

        {/* Left: Logo */}
        <div className="flex-1 basis-0 flex items-center justify-start min-w-max">
          <Link to="/" className="flex items-center group cursor-pointer h-8 sm:h-9 md:h-9 lg:h-10">
            <SystemLogo className="h-full max-w-full" sizeClassName="h-full w-auto max-w-full object-contain" useTextLogo={true} showText={true} />
          </Link>
        </div>

        {/* Center: Desktop Nav Links */}
        <div className="hidden md:flex items-center justify-center gap-0.5 lg:gap-1.5 xl:gap-2.5 flex-shrink-0 mx-1 lg:mx-2">
          {navLinks.map((link, index) => {
            const isActive = activeNavIndex === index;
            const isDistribution = link.label === '发行服务';

            if (isActive) {
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={(e) => {
                    if (isDistribution) {
                      handleDistributionClick(e);
                    }
                  }}
                  className="group relative px-2.5 py-1.5 lg:px-4 lg:py-2 xl:px-5 xl:py-2 rounded-full font-bold text-xs lg:text-sm xl:text-base text-slate-900 transition-all duration-300 flex items-center gap-0.5 lg:gap-1 bg-gradient-to-r from-[#eef9c3] via-[#d4f8db] to-[#aef5e7] shadow-[0_0_20px_rgba(168,230,207,0.85)] cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <span>{link.label}</span>
                  {link.hasSparkle && (
                    <SparkleIcon className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-[#eab308] transform -translate-y-1 lg:-translate-y-1.5" />
                  )}
                </Link>
              );
            }

            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={(e) => {
                  if (isDistribution) {
                    handleDistributionClick(e);
                  }
                }}
                className="group relative px-2 py-1.5 lg:px-3.5 lg:py-2 xl:px-4.5 xl:py-2 rounded-full font-medium text-xs lg:text-sm xl:text-base text-slate-700 hover:text-slate-950 transition-all duration-200 hover:bg-slate-100/70 flex items-center cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <span>{link.label}</span>
                {link.hasSparkle && (
                  <SparkleIcon className="w-2.5 h-2.5 lg:w-3 lg:h-3 text-[#facc15] transform -translate-y-1 lg:-translate-y-1.5 ml-0.5 opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all duration-300" />
                )}
              </Link>
            );
          })}
        </div>

        {/* Right: Auth / User + Mobile Menu Button */}
        <div className="flex-1 basis-0 flex items-center justify-end gap-2 sm:gap-3 lg:gap-4 min-w-max">
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            {isLoggedIn && user ? (
              <>
                {/* 会员积分 / 每日签到展示（终身免费/不限积分用户隐藏） */}
                {!isUnlimitedPoints && <UserPointsBadge />}

                {/* Notifications Popover */}
                <NotificationPopover />

                {/* User Menu */}
                <UserMenu onOpenVipModal={() => openMembershipModal()} />
              </>
            ) : (
              <Link
                to="/auth"
                className="group px-3 py-1.5 lg:px-5 lg:py-2 xl:px-6 xl:py-2.5 rounded-full font-semibold text-xs lg:text-sm xl:text-base text-slate-800 transition-all duration-300 flex items-center gap-1 lg:gap-2 bg-gradient-to-r from-[#ebfada] via-[#c6f6e1] to-[#9bf4e7] hover:brightness-105 shadow-[0_3px_15px_rgba(160,240,210,0.5)] hover:shadow-[0_6px_22px_rgba(140,230,200,0.7)] cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <SparkleIcon className="w-3 h-3 lg:w-4 lg:h-4 text-[#facc15]" />
                <span>登录 / 注册</span>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl text-slate-800 ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div className="pointer-events-auto absolute top-full left-4 right-4 mt-2 bg-white/95 backdrop-blur-xl border border-white/90 rounded-2xl p-4 shadow-xl md:hidden animate-[fadeIn_0.2s_ease-out]">
          <div className="flex flex-col gap-1.5">
            {navLinks.map((link, index) => {
              const isActive = activeNavIndex === index;
              const isDistribution = link.label === '发行服务';
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={(e) => {
                    setMenuOpen(false);
                    if (isDistribution) {
                      handleDistributionClick(e);
                    }
                  }}
                  className={`group px-4 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer flex items-center justify-between ${isActive
                    ? 'bg-gradient-to-r from-[#eef9c3] via-[#d4f8db] to-[#aef5e7] text-slate-900 font-bold'
                    : 'text-slate-700 hover:bg-slate-100'
                    }`}
                >
                  <span>{link.label}</span>
                  {link.hasSparkle && (
                    <SparkleIcon
                      className={`w-3 h-3 text-[#facc15] ${isActive
                        ? ''
                        : 'opacity-0 group-hover:opacity-100 scale-50 group-hover:scale-100 transition-all duration-300'
                        }`}
                    />
                  )}
                </Link>
              );
            })}

            {isLoggedIn && user ? (
              <>
                <div className="border-t border-slate-100 my-2 pt-2 flex items-center justify-between px-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                      {avatarUrl ? (
                        <img src={avatarUrl} className="w-full h-full rounded-full object-cover" alt={user.nick_name || user.name} />
                      ) : (
                        (user.nick_name || user.name).slice(0, 1)
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-800">{user.nick_name || user.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Link
                      to="/point-transactions"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 font-bold border border-amber-200/80"
                      title="查看积分流水"
                    >
                      <i className="ri-coin-line text-amber-500"></i>
                      {totalPoints} 积分
                    </Link>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        openMembershipModal({ defaultTab: 'membership' });
                      }}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-medium cursor-pointer"
                    >
                      <i className="ri-vip-crown-line"></i>
                      VIP
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 rounded-xl text-sm text-slate-500 hover:bg-slate-100"
                >
                  退出登录
                </button>
              </>
            ) : (
              <div className="border-t border-slate-100 my-2 pt-2">
                <Link
                  to="/auth"
                  className="group w-full px-4 py-2.5 rounded-xl text-sm font-medium text-slate-800 bg-gradient-to-r from-[#ebfada] via-[#c6f6e1] to-[#9bf4e7] flex items-center justify-center gap-2"
                >
                  <SparkleIcon className="w-3.5 h-3.5 text-[#facc15]" />
                  <span>登录 / 注册</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
      {/* 全局会员与积分充值弹窗 */}
      <MembershipModal
        isOpen={vipModalOpen}
        onClose={closeMembershipModal}
        defaultTab={defaultTab}
        defaultMemberType={defaultMemberType}
      />
    </header>
  );
}

export default function Navbar() {
  const navbarAlreadyRendered = useContext(NavbarRenderedContext);
  return navbarAlreadyRendered ? null : <NavbarContent />;
}

